import { Token } from "@/entities/settings/types";
import {
  ACCOUNT_SERVICES,
  Service
} from "@/pages/settings/components/connectedAccounts/types";

export interface ServiceConnection {
  hasTokens: boolean;
  allTokensValid: boolean;
  tokenUser?: string;
}

// A service owns its exact id and its dotted sub-services (google.calendar), but
// not other services sharing a name prefix (zoominfo is not part of zoom).
export const belongsToService = (
  tokenService: string | undefined,
  serviceId: Service
) => tokenService === serviceId || !!tokenService?.startsWith(`${serviceId}.`);

// The first of a service's tokens that is no longer valid, if any.
export const findInvalidServiceToken = (
  tokens: Token[] | undefined,
  serviceId: Service
): Token | undefined =>
  tokens?.find(
    (token) => belongsToService(token?.service, serviceId) && !token?.isValid
  );

// A service is connected only when it has tokens and every one of them is still valid.
export const getServiceConnection = (
  tokens: Token[] | undefined,
  serviceId: Service
): ServiceConnection => {
  const serviceTokens =
    tokens?.filter((token) => belongsToService(token.service, serviceId)) ?? [];
  const hasTokens = serviceTokens.length > 0;

  return {
    hasTokens,
    allTokensValid: hasTokens && serviceTokens.every((token) => token.isValid),
    tokenUser: serviceTokens[0]?.user
  };
};

// Account-level services are shared by the whole account, so only a Settings
// admin may disconnect them; anyone may disconnect their own services.
export const canDisconnectService = (
  serviceId: Service,
  isSettingsAdmin: boolean
) => !ACCOUNT_SERVICES.includes(serviceId) || isSettingsAdmin;
